import { useState } from 'react';
import { useCatalogSearch, useCatalogTags, useCatalogEntries, useCatalogSchemas } from '@/hooks/useCatalog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatBytes, formatDate } from '@/lib/utils';
import { SkeletonCardGrid } from '@/components/skeletons/SkeletonLoaders';
import { EmptyCatalogState, EmptySearchResultsState, ErrorState } from '@/components/EmptyState';
import { Database, Tag, Calendar, HardDrive, Table2, Columns, Layers } from 'lucide-react';
import type { Dataset, PaginatedResponse, CatalogEntriesResponse } from '@/types';

type CatalogTab = 'datasets' | 'tables';

export const Catalog = () => {
  const [activeTab, setActiveTab] = useState<CatalogTab>('datasets');

  // Dataset state
  const [datasetSearch, setDatasetSearch] = useState('');
  const [activeDatasetSearch, setActiveDatasetSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [datasetPage, setDatasetPage] = useState(1);

  // Tables state
  const [tableSearch, setTableSearch] = useState('');
  const [activeTableSearch, setActiveTableSearch] = useState('');
  const [selectedSchema, setSelectedSchema] = useState<string | undefined>();
  const [tablePage, setTablePage] = useState(1);

  // Dataset queries
  const {
    data: datasetData,
    isLoading: datasetsLoading,
    error: datasetsError,
    refetch: refetchDatasets,
  } = useCatalogSearch({
    query: activeDatasetSearch,
    tags: selectedTags,
    page: datasetPage,
    limit: 20,
  });

  const { data: availableTags, isLoading: tagsLoading } = useCatalogTags();

  // Table queries
  const {
    data: tablesData,
    isLoading: tablesLoading,
    error: tablesError,
    refetch: refetchTables,
  } = useCatalogEntries({
    search: activeTableSearch,
    schema_name: selectedSchema,
    page: tablePage,
    limit: 20,
  });

  const { data: availableSchemas, isLoading: schemasLoading } = useCatalogSchemas();

  const typedDatasetData = datasetData as PaginatedResponse<Dataset> | undefined;
  const typedTablesData = tablesData as CatalogEntriesResponse | undefined;

  // Dataset handlers
  const handleDatasetSearch = () => {
    setActiveDatasetSearch(datasetSearch);
    setDatasetPage(1);
  };

  const handleClearDatasetSearch = () => {
    setDatasetSearch('');
    setActiveDatasetSearch('');
    setSelectedTags([]);
    setDatasetPage(1);
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
    setDatasetPage(1);
  };

  // Table handlers
  const handleTableSearch = () => {
    setActiveTableSearch(tableSearch);
    setTablePage(1);
  };

  const handleClearTableSearch = () => {
    setTableSearch('');
    setActiveTableSearch('');
    setSelectedSchema(undefined);
    setTablePage(1);
  };

  const handleSchemaSelect = (schema: string) => {
    setSelectedSchema(selectedSchema === schema ? undefined : schema);
    setTablePage(1);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Data Catalog</h1>
        <p className="text-muted-foreground">
          Search and explore your data assets
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as CatalogTab)}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="datasets" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            Datasets
          </TabsTrigger>
          <TabsTrigger value="tables" className="flex items-center gap-2">
            <Table2 className="h-4 w-4" />
            DuckLake Tables
          </TabsTrigger>
        </TabsList>

        {/* Datasets Tab */}
        <TabsContent value="datasets" className="space-y-6">
          {datasetsLoading ? (
            <>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex gap-2">
                    <Input placeholder="Search datasets..." value="" disabled className="flex-1" />
                    <Button disabled>Search</Button>
                  </div>
                </CardContent>
              </Card>
              <SkeletonCardGrid count={6} />
            </>
          ) : datasetsError ? (
            <ErrorState
              message={datasetsError instanceof Error ? datasetsError.message : 'Failed to load datasets'}
              onRetry={() => refetchDatasets()}
            />
          ) : (
            <>
              {/* Dataset Search */}
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search datasets by name, description..."
                      value={datasetSearch}
                      onChange={(e) => setDatasetSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleDatasetSearch()}
                      className="flex-1"
                    />
                    <Button onClick={handleDatasetSearch}>Search</Button>
                    {(activeDatasetSearch || selectedTags.length > 0) && (
                      <Button variant="outline" onClick={handleClearDatasetSearch}>
                        Clear
                      </Button>
                    )}
                  </div>

                  {/* Tag Filters */}
                  {!tagsLoading && availableTags && availableTags.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Filter by tags:</div>
                      <div className="flex flex-wrap gap-2">
                        {availableTags.map((tag) => (
                          <Badge
                            key={tag}
                            variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => handleTagToggle(tag)}
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Active Filters Display */}
              {(activeDatasetSearch || selectedTags.length > 0) && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Showing results for:</span>
                  {activeDatasetSearch && (
                    <Badge variant="secondary">
                      Search: "{activeDatasetSearch}"
                    </Badge>
                  )}
                  {selectedTags.map((tag) => (
                    <Badge key={tag} variant="secondary">
                      Tag: {tag}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Dataset Results */}
              {(!typedDatasetData?.items || typedDatasetData.items.length === 0) ? (
                activeDatasetSearch || selectedTags.length > 0 ? (
                  <EmptySearchResultsState onClear={handleClearDatasetSearch} />
                ) : (
                  <EmptyCatalogState />
                )
              ) : (
                <>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {typedDatasetData.items.map((asset) => (
                      <Card key={asset.id} className="hover:shadow-md transition-shadow">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Database className="h-5 w-5 text-primary" />
                            <span className="truncate">{asset.name}</span>
                          </CardTitle>
                          <CardDescription className="line-clamp-2">
                            {asset.description || 'No description available'}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <HardDrive className="h-4 w-4" />
                              <span>
                                {asset.row_count?.toLocaleString() || '0'} rows
                                {asset.size_bytes && ` • ${formatBytes(asset.size_bytes)}`}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Calendar className="h-4 w-4" />
                              <span>Created {formatDate(asset.created_at)}</span>
                            </div>
                          </div>

                          {asset.tags && asset.tags.length > 0 && (
                            <div className="flex items-start gap-2">
                              <Tag className="h-4 w-4 text-muted-foreground mt-0.5" />
                              <div className="flex flex-wrap gap-1">
                                {asset.tags.map((tag) => (
                                  <Badge key={tag} variant="secondary" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {asset.source_name && (
                            <div className="text-sm text-muted-foreground">
                              Source: <span className="font-medium">{asset.source_name}</span>
                            </div>
                          )}

                          <div className="pt-2">
                            <Badge
                              variant={asset.status === 'active' ? 'default' : 'outline'}
                              className={
                                asset.status === 'active'
                                  ? 'bg-success/10 text-success hover:bg-success/10'
                                  : ''
                              }
                            >
                              {asset.status}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Dataset Pagination */}
                  {typedDatasetData.total > 20 && (
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setDatasetPage((p) => Math.max(1, p - 1))}
                        disabled={datasetPage === 1}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {datasetPage} of {typedDatasetData.pages}
                      </span>
                      <Button
                        variant="outline"
                        onClick={() => setDatasetPage((p) => p + 1)}
                        disabled={datasetPage >= typedDatasetData.pages}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </TabsContent>

        {/* DuckLake Tables Tab */}
        <TabsContent value="tables" className="space-y-6">
          {tablesLoading ? (
            <>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex gap-2">
                    <Input placeholder="Search tables..." value="" disabled className="flex-1" />
                    <Button disabled>Search</Button>
                  </div>
                </CardContent>
              </Card>
              <SkeletonCardGrid count={6} />
            </>
          ) : tablesError ? (
            <ErrorState
              message={tablesError instanceof Error ? tablesError.message : 'Failed to load tables'}
              onRetry={() => refetchTables()}
            />
          ) : (
            <>
              {/* Tables Search */}
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search tables by name..."
                      value={tableSearch}
                      onChange={(e) => setTableSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleTableSearch()}
                      className="flex-1"
                    />
                    <Button onClick={handleTableSearch}>Search</Button>
                    {(activeTableSearch || selectedSchema) && (
                      <Button variant="outline" onClick={handleClearTableSearch}>
                        Clear
                      </Button>
                    )}
                  </div>

                  {/* Schema Filters */}
                  {!schemasLoading && availableSchemas && availableSchemas.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Filter by schema:</div>
                      <div className="flex flex-wrap gap-2">
                        {availableSchemas.map((schema) => (
                          <Badge
                            key={schema}
                            variant={selectedSchema === schema ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => handleSchemaSelect(schema)}
                          >
                            {schema}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Active Filters Display */}
              {(activeTableSearch || selectedSchema) && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Showing results for:</span>
                  {activeTableSearch && (
                    <Badge variant="secondary">
                      Search: "{activeTableSearch}"
                    </Badge>
                  )}
                  {selectedSchema && (
                    <Badge variant="secondary">
                      Schema: {selectedSchema}
                    </Badge>
                  )}
                </div>
              )}

              {/* Tables Results */}
              {(!typedTablesData?.items || typedTablesData.items.length === 0) ? (
                activeTableSearch || selectedSchema ? (
                  <EmptySearchResultsState onClear={handleClearTableSearch} />
                ) : (
                  <div className="text-center py-12">
                    <Table2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No DuckLake Tables Found</h3>
                    <p className="text-muted-foreground max-w-md mx-auto">
                      No tables are registered in the DuckLake catalog yet.
                      Tables will appear here once data is ingested.
                    </p>
                  </div>
                )
              ) : (
                <>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {typedTablesData.items.map((entry) => (
                      <Card key={entry.id} className="hover:shadow-md transition-shadow">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Table2 className="h-5 w-5 text-primary" />
                            <span className="truncate">{entry.name}</span>
                          </CardTitle>
                          <CardDescription className="flex items-center gap-2">
                            <Layers className="h-3 w-3" />
                            {entry.schema_name}.{entry.name}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="space-y-2 text-sm">
                            {entry.row_count !== null && entry.row_count !== undefined && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <HardDrive className="h-4 w-4" />
                                <span>{entry.row_count.toLocaleString()} rows</span>
                              </div>
                            )}
                            {entry.column_count !== null && entry.column_count !== undefined && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Columns className="h-4 w-4" />
                                <span>{entry.column_count} columns</span>
                              </div>
                            )}
                          </div>

                          {/* Table Type Badge */}
                          <div className="pt-2 flex items-center gap-2">
                            <Badge variant="outline">
                              {entry.table_type}
                            </Badge>
                            <Badge
                              variant="secondary"
                              className="bg-info/10 text-info"
                            >
                              {entry.catalog_name}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Tables Pagination */}
                  {typedTablesData.pagination.total > 20 && (
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setTablePage((p) => Math.max(1, p - 1))}
                        disabled={tablePage === 1}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {tablePage} of {typedTablesData.pagination.pages}
                      </span>
                      <Button
                        variant="outline"
                        onClick={() => setTablePage((p) => p + 1)}
                        disabled={!typedTablesData.pagination.has_next}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
